import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { readinessActions as A } from '../../store/readinessSlice';
import { EMAIL_PROMISE } from '../../config/readinessQuestions';
import {
  matchPrograms,
  assessmentProgress,
  scoreLabelFor,
} from '../../utils/readinessAssessment';

function Modal({ maxClass, onClose, children }) {
  return (
    <div className="rdz-modal-overlay" onClick={onClose}>
      <div className={'rdz-modal ' + maxClass} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/**
 * The three Readiness modals: email gate (account creation), passwordless
 * sign-in (email → 8-digit code), and email-my-report.
 */
export default function ReadinessModals({ score, controller }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    gate, signin, signinStep, signinError, code, report, reportSent, email, answers, done, leave,
  } = useSelector((s) => s.readiness);

  const emailValid = email.includes('@');
  const { answered, total } = assessmentProgress(answers);

  return (
    <>
      {/* ── Email gate (account creation) ─────────────────────── */}
      {gate && (
        <Modal maxClass="rdz-modal--gate" onClose={() => dispatch(A.closeGate())}>
          <div className="rdz-modal__head">
            <div className="rdz-modal__title">Save your progress to continue</div>
            <button type="button" className="rdz-modal__close" onClick={() => dispatch(A.closeGate())}>&#215;</button>
          </div>
          <p className="rdz-modal__body rdz-modal__body--tight">
            The full application saves as you go, so we need one thing: an email address. That&apos;s
            your whole account &mdash; no password, ever. We email you a secure code when you come back.
          </p>
          <div className="rdz-promise">
            <div className="rdz-promise__title">{EMAIL_PROMISE.title}</div>
            <div className="rdz-promise__body">{EMAIL_PROMISE.body}</div>
          </div>
          <div className="rdz-modal__row">
            <input
              className="rdz-modal__input"
              value={email}
              onChange={(e) => dispatch(A.setEmail(e.target.value))}
              onKeyDown={(e) => { if (e.key === 'Enter') controller.gateSubmit(); }}
              placeholder="you@email.com"
              type="email"
            />
            <button
              type="button"
              className={'rdz-modal__btn' + (emailValid ? '' : ' rdz-dim')}
              onClick={() => controller.gateSubmit()}
            >
              Continue
            </button>
          </div>
        </Modal>
      )}

      {/* ── Leave for homepage (save progress first?) ─────────── */}
      {leave && (
        <Modal maxClass="rdz-modal--leave" onClose={() => dispatch(A.closeLeave())}>
          <div className="rdz-modal__head">
            <div className="rdz-modal__title">Before you go &mdash; save your progress?</div>
            <button type="button" className="rdz-modal__close" onClick={() => dispatch(A.closeLeave())}>&#215;</button>
          </div>
          <p className="rdz-modal__body rdz-modal__body--tight">
            Your conversation with Owen isn&apos;t saved yet. Enter your email and we&apos;ll keep
            everything so you can pick up right where you left off &mdash; no password, ever.
          </p>
          <div className="rdz-promise">
            <div className="rdz-promise__title">{EMAIL_PROMISE.title}</div>
            <div className="rdz-promise__body">{EMAIL_PROMISE.body}</div>
          </div>
          <div className="rdz-modal__row">
            <input
              className="rdz-modal__input"
              value={email}
              onChange={(e) => dispatch(A.setEmail(e.target.value))}
              onKeyDown={(e) => { if (e.key === 'Enter' && controller.leaveSave()) navigate('/'); }}
              placeholder="you@email.com"
              type="email"
            />
            <button
              type="button"
              className={'rdz-modal__btn' + (emailValid ? '' : ' rdz-dim')}
              onClick={() => { if (controller.leaveSave()) navigate('/'); }}
            >
              Save &amp; go
            </button>
          </div>
          <p className="rdz-modal__leave-link">
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); dispatch(A.closeLeave()); navigate('/'); }}
            >
              Leave without saving
            </a>
          </p>
        </Modal>
      )}

      {/* ── Sign in (passwordless) ─────────────────────────────── */}
      {signin && (
        <Modal maxClass="rdz-modal--signin" onClose={() => dispatch(A.closeSignin())}>
          <div className="rdz-modal__head">
            <div className="rdz-modal__title">Sign in</div>
            <button type="button" className="rdz-modal__close" onClick={() => dispatch(A.closeSignin())}>&#215;</button>
          </div>
          {signinStep === 0 && (
            <>
              <p className="rdz-modal__body">
                No password &mdash; ever. Enter your email and we&apos;ll send you a one-time secure code.
              </p>
              <div className="rdz-modal__row">
                <input
                  className="rdz-modal__input"
                  value={email}
                  onChange={(e) => dispatch(A.setEmail(e.target.value))}
                  onKeyDown={(e) => { if (e.key === 'Enter') controller.sendCode(); }}
                  placeholder="you@email.com"
                  type="email"
                />
                <button
                  type="button"
                  className={'rdz-modal__btn' + (emailValid ? '' : ' rdz-dim')}
                  onClick={() => controller.sendCode()}
                >
                  Email me a code
                </button>
              </div>
            </>
          )}
          {signinStep === 1 && (
            <>
              <p className="rdz-modal__body">
                We sent an 8-digit code to <strong>{email}</strong>. It expires in 10 minutes.
              </p>
              <div className="rdz-modal__row">
                <input
                  className="rdz-modal__input rdz-modal__input--code"
                  value={code}
                  onChange={(e) => dispatch(A.setCode(e.target.value))}
                  onKeyDown={(e) => { if (e.key === 'Enter') controller.codeSubmit(); }}
                  placeholder="00000000"
                  inputMode="numeric"
                  maxLength={8}
                />
                <button
                  type="button"
                  className={'rdz-modal__btn' + (code.length === 8 ? '' : ' rdz-dim')}
                  onClick={() => controller.codeSubmit()}
                >
                  Sign in
                </button>
              </div>
              <p className="rdz-modal__resend">
                Didn&apos;t get it?{' '}
                <a href="#resend" onClick={(e) => { e.preventDefault(); controller.sendCode(); }}>Resend code</a>
              </p>
            </>
          )}
          {signinError && <p className="rdz-modal__error">{signinError}</p>}
          <p className="rdz-modal__fine">
            We never market, sell, or email anything that didn&apos;t come from your direction.
          </p>
        </Modal>
      )}

      {/* ── Email report ───────────────────────────────────────── */}
      {report && (
        <Modal maxClass="rdz-modal--report" onClose={() => dispatch(A.closeReport())}>
          {reportSent ? (
            <div className="rdz-report-sent">
              <div className="rdz-report-sent__check">&#10003;</div>
              <div className="rdz-report-sent__title">Report on its way</div>
              <div className="rdz-report-sent__body">
                Your readiness report was sent to <strong style={{ fontWeight: 600, color: '#3f3f46' }}>{email}</strong>.
                It covers everything you&apos;ve completed so far &mdash; pick up right where you left
                off, anytime.
              </div>
              <button type="button" className="rdz-report-sent__done" onClick={() => dispatch(A.closeReport())}>
                Done
              </button>
            </div>
          ) : (
            <>
              <div className="rdz-modal__head">
                <div className="rdz-modal__title">Email my progress report</div>
                <button type="button" className="rdz-modal__close" onClick={() => dispatch(A.closeReport())}>&#215;</button>
              </div>
              <p className="rdz-modal__body">
                A snapshot of where you stand right now &mdash; yours to keep, share, or come back to.
              </p>
              <div className="rdz-report-lines">
                {[
                  answered + ' of ' + total + ' questions answered',
                  'Readiness Score: ' + score + ' \u2014 ' + scoreLabelFor(score),
                  done
                    ? 'Program matches: ' + matchPrograms(answers).map((p) => p.name).join(', ')
                    : 'Program matches unlock after your last question',
                ].map((text) => (
                  <div className="rdz-report-line" key={text}>
                    <span className="rdz-report-line__dot" />
                    <span className="rdz-report-line__text">{text}</span>
                  </div>
                ))}
              </div>
              <div className="rdz-modal__row">
                <input
                  className="rdz-modal__input"
                  value={email}
                  onChange={(e) => dispatch(A.setEmail(e.target.value))}
                  onKeyDown={(e) => { if (e.key === 'Enter') controller.sendReport(); }}
                  placeholder="you@email.com"
                  type="email"
                />
                <button
                  type="button"
                  className={'rdz-modal__btn' + (emailValid ? '' : ' rdz-dim')}
                  onClick={() => controller.sendReport()}
                >
                  Send
                </button>
              </div>
              <p className="rdz-modal__fine">
                We&apos;ll never share your email with lenders. One report, no marketing.
              </p>
            </>
          )}
        </Modal>
      )}
    </>
  );
}
